import React, { useEffect } from "react";
import { Link } from "react-router-dom";

// ============================================================================
// SEO METADATA
// ============================================================================
import BLOG_SEO from "@/data/blogSeo.json";
import { blogOgImage } from "@/components/RouteSeo";
const PAGE_URL = "https://mindbodyspirithygienetools.com/blog/the-messy-middle-is-the-door";
const PAGE_TITLE = BLOG_SEO.legacy["the-messy-middle-is-the-door"].title;
const PAGE_DESC = BLOG_SEO.legacy["the-messy-middle-is-the-door"].description;
const PAGE_IMAGE = blogOgImage("the-messy-middle-is-the-door");
const PUBLISH_DATE = "2026-05-28";

const buildSchemas = () => [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": PAGE_URL,
    headline: "The Messy Middle Is the Door — Beginning Again After Loss",
    description: PAGE_DESC,
    url: PAGE_URL,
    datePublished: PUBLISH_DATE,
    dateModified: PUBLISH_DATE,
    image: PAGE_IMAGE,
    author: {
      "@type": "Person",
      name: "Temple Franklin",
      url: "https://mindbodyspirithygienetools.com/about",
    },
    publisher: {
      "@type": "Organization",
      name: "Mind Body Spirit HYGIENE™ Tools",
      logo: { "@type": "ImageObject", url: "https://mindbodyspirithygienetools.com/logo512.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": PAGE_URL },
    keywords: "how to start over after loss, beginning again midlife, messy middle of grief, rebuilding after trauma, Temple Franklin, Mind Body Spirit HYGIENE, recovering people pleaser, Hashimoto, menopause story, stop abandoning yourself",
    articleSection: "Personal Essay",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://mindbodyspirithygienetools.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://mindbodyspirithygienetools.com/blog" },
      { "@type": "ListItem", position: 3, name: "The Messy Middle Is the Door", item: PAGE_URL },
    ],
  },
];

import BlogCalendarOptin from "@/components/BlogCalendarOptin";

// We need this to import the calendar opt-in component at the bottom.
const BlogMessyMiddle = () => {
  useEffect(() => {
    document.title = PAGE_TITLE;

    const upsert = (key, attr, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    upsert("description", "name", PAGE_DESC);
    upsert(
      "keywords",
      "name",
      "how to start over after loss, beginning again midlife, messy middle of grief, rebuilding after trauma, Temple Franklin, Hashimoto's story, menopause, recovering people pleaser, Mind Body Spirit HYGIENE"
    );
    upsert("og:type", "property", "article");
    upsert("og:title", "property", PAGE_TITLE);
    upsert("og:description", "property", PAGE_DESC);
    upsert("og:url", "property", PAGE_URL);
    upsert("og:image", "property", PAGE_IMAGE);
    upsert("og:site_name", "property", "Mind Body Spirit HYGIENE™ Tools");
    upsert("article:author", "property", "Temple Franklin");
    upsert("article:published_time", "property", PUBLISH_DATE);
    upsert("twitter:card", "name", "summary_large_image");
    upsert("twitter:title", "name", PAGE_TITLE);
    upsert("twitter:description", "name", PAGE_DESC);
    upsert("twitter:image", "name", PAGE_IMAGE);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", PAGE_URL);

    const scripts = buildSchemas().map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-blog-messy-seo", "1");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.querySelectorAll('script[data-blog-messy-seo="1"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <article className="min-h-screen bg-[#efeae7]" data-testid="blog-messy-middle-page">
      {/* HERO */}
      <header className="pt-32 pb-12 px-6 text-center" data-testid="blog-hero">
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-[11px] tracking-[0.42em] uppercase text-[#068fa2] font-semibold mb-6"
            data-testid="blog-eyebrow"
          >
            A NOTE FROM TEMPLE FRANKLIN · 2026
          </p>
          <h1
            className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter text-[#042F2E] leading-[1.0] mb-8"
            data-testid="blog-headline"
          >
            The Messy Middle <span className="italic text-[#068fa2]">Is the Door</span>
          </h1>
          <p className="font-serif italic text-lg md:text-xl text-[#1E2A29] max-w-xl mx-auto leading-relaxed" data-testid="blog-subhead">
            Beginning again after grief, loss, illness, and trauma.
          </p>

          <div className="mt-12 mx-auto flex items-center justify-center gap-3" data-testid="blog-meta">
            <div className="h-px w-12 bg-[#d4af6a]/60" />
            <span className="text-[11px] tracking-[0.32em] uppercase text-[#5F7370] font-medium">
              5 min read
            </span>
            <div className="h-px w-12 bg-[#d4af6a]/60" />
          </div>
        </div>
      </header>

      {/* BODY */}
      <section className="px-6 pb-20" data-testid="blog-body">
        <div className="max-w-[680px] mx-auto font-serif text-[1.18rem] md:text-[1.22rem] leading-[1.85] text-[#042F2E]">

          <p className="mb-7">
            It was a hot afternoon in Arizona when I opened a laptop. I did not know where I was going in life and lifestyle. I did know I was curious. I did know I am a creator. So even though I had no idea what I was doing, I got curious and began again — a new learning skill, new thoughts, new tools. Anthropic and Claude. Curating conversation and questions.
          </p>

          <p className="mb-7">
            Was I excited? No. And yet I said to myself over and over: <em>"I GET TO."</em> This fostered appreciation in self and in my abilities as I fought through fatigue and fuzzy brain. Yes, there have been many days when I started and had to stop and rest my body. I simply wasn't doing myself any good, and I had to honor her to heal.
          </p>

          <p className="mb-7">
            I had been up and down and awake for months — literally flushed with menopause. Yes, so exhausted and uninterested in anything. My body was still healing from Hashimoto's, grief, and trauma — carrying the weight of losses. My heart was carrying the loss of family both living and in death. The life I created in my family. The kind of grief that does not announce itself politely. The loss of living family members. The betrayal of family. The death and betrayal of my mother, who raised me.
          </p>

          <p className="mb-7">
            I did not really have a plan. I did not have a marketing degree or a tech background or a body of work to lean on.
          </p>

          <p className="mb-7">
            I had pure, authentic <strong className="font-semibold">curiosity</strong>. And I had the fact that I was still here. My conscious choice: <em>I am worth fighting for. I GET TO.</em> Not toxic positivity. I gave myself grace and honored her when it was time to rest and rejuvenate.
          </p>

          {/* Section divider */}
          <div className="my-16 flex items-center justify-center gap-4" aria-hidden="true">
            <div className="h-px w-16 bg-[#d4af6a]/40" />
            <span className="text-[#d4af6a]">✦</span>
            <div className="h-px w-16 bg-[#d4af6a]/40" />
          </div>

          <h2 className="font-serif italic text-3xl md:text-4xl text-[#042F2E] text-center leading-tight mb-10" data-testid="section-rebuilding">
            What I Learned From the Rebuilding
          </h2>

          <h3 className="font-serif italic text-2xl md:text-[1.7rem] text-[#068fa2] mb-5 mt-12">
            "I have to" became "I GET TO."
          </h3>

          <p className="mb-7">
            In the beginning, every task was a <em>have to</em>. <em>I have to figure out the website. I have to learn this tool. I have to keep going.</em> The exhaustion of <em>have to</em> was its own kind of guilt.
          </p>

          <p className="mb-7">
            I walk my own talk — the phrase shifted. <em>I get to. I get to build something honest. I get to use the loss for something other than grief. I get to be the woman creating again.</em>
          </p>

          <h3 className="font-serif italic text-2xl md:text-[1.7rem] text-[#068fa2] mb-5 mt-14">
            Stop abandoning yourself.
          </h3>

          <p className="mb-7">
            For most of my life, kindness meant putting myself last — and the takers in my life loved it, until I no longer did. The illness was the moment my body refused to participate in the abandoning anymore. She was done, and she needed healing. The trauma was the moment my soul refused. Grief was the final teacher that said: <em>you are not allowed to keep leaving yourself.</em>
          </p>

          <p className="mb-7">
            I would check in with the Divine and ask, <em>"When can I start again? What can I do now?"</em> I was so used to the <em>doing</em>. Divine response: <strong className="font-semibold">Heal.</strong>
          </p>

          <p className="mb-7">
            Recovering people-pleaser here. The rebuild was, at its center, the practice of un-abandoning. Of learning self-love. Of creating and understanding who I was. Of speaking my truth — figuring out what that even was. Of her boundaries, her distance, her self-love, her forgiveness, and her respect.
          </p>

          {/* Pull-quote — "The buck stops here." */}
          <div
            className="my-14 py-10 px-6 text-center bg-[#042F2E] rounded-2xl"
            data-testid="pullquote-buck"
          >
            <p className="font-serif italic text-3xl md:text-4xl text-[#efeae7] leading-tight">
              The buck stops here.
            </p>
          </div>

          {/* Section divider */}
          <div className="my-16 flex items-center justify-center gap-4" aria-hidden="true">
            <div className="h-px w-16 bg-[#d4af6a]/40" />
            <span className="text-[#d4af6a]">✦</span>
            <div className="h-px w-16 bg-[#d4af6a]/40" />
          </div>

          <h2 className="font-serif italic text-3xl md:text-4xl text-[#042F2E] text-center leading-tight mb-10" data-testid="section-woman-reading">
            What I Want the Woman Reading This to Hear
          </h2>

          <p className="mb-7">
            If you are in the messy middle right now — at the kitchen table, in the parking lot, in the bathroom you locked yourself in for one quiet minute — I am not here to remind you it will all make sense.
          </p>

          <p className="mb-7">
            I am here to remind you: <strong className="font-semibold">the messy middle is a doorway, not the detour.</strong>
          </p>

          <p className="mb-7">
            You are not late. You are not behind. You are not too broken. You are at the threshold most women never cross, because they decide too soon that the door is the wall. That wall could be built with fears, doubts, and the sticky stuff we create in our lives.
          </p>

          <p className="mb-7">
            Begin with curiosity. Not certainty. Curiosity is the only credential you actually need. This is not toxic positivity — take a breath, take a break, take a nap, walk in nature, fuel your mind, body, and soul up, then begin again.
          </p>

          <p className="mb-7">
            Find one thing — one — that lights you up. Even a flicker. <em>"That squeaky wheel."</em>
          </p>

          <p className="mb-7">
            Take the next small step. Just one. Then, by compounding those small steps, you create a solid foundation. And you keep learning. And you keep building.
          </p>

          {/* Signature */}
          <div className="mt-16 mb-4 text-center" data-testid="blog-signature">
            <p className="font-serif italic text-xl text-[#042F2E] mb-2">Blessings,</p>
            <p className="font-serif italic text-2xl text-[#068fa2]">Temple</p>
          </div>
        </div>
      </section>

      {/* ── THE 3 DOORS · FUNNEL ─────────────────────────────────────────── */}
      <section className="px-6 pb-24 pt-4" data-testid="blog-doors">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="mx-auto flex items-center justify-center gap-4 mb-7" aria-hidden="true">
              <div className="h-px w-16 bg-[#d4af6a]/50" />
              <span className="text-[#d4af6a]">✦</span>
              <div className="h-px w-16 bg-[#d4af6a]/50" />
            </div>
            <p className="font-serif italic text-2xl md:text-3xl text-[#1E2A29]" data-testid="doors-eyebrow">
              If any of this landed —
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Door 1 — Pull a card */}
            <Link
              to="/draw"
              data-testid="door-draw"
              className="group block bg-white rounded-3xl p-8 shadow-sm border border-[#042F2E]/8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="text-3xl mb-4" aria-hidden="true">🪶</div>
              <h3 className="font-serif italic text-2xl text-[#042F2E] mb-3 leading-tight">
                Pull a card.
              </h3>
              <p className="font-serif text-[15px] text-[#1E2A29] leading-relaxed mb-5">
                The 58-card Mind Body Spirit HYGIENE™ deck. One card. One message. Yours if it lands.
              </p>
              <p className="font-sans text-xs tracking-[0.28em] uppercase text-[#068fa2] font-semibold group-hover:underline underline-offset-4">
                /draw →
              </p>
            </Link>

            {/* Door 2 — Free grief printable */}
            <Link
              to="/grief-printable"
              data-testid="door-grief"
              className="group block bg-[#042F2E] rounded-3xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="text-3xl mb-4" aria-hidden="true">📓</div>
              <h3 className="font-serif italic text-2xl text-[#efeae7] mb-3 leading-tight">
                The free grief printable.
              </h3>
              <p className="font-serif text-[15px] text-[#efeae7]/80 leading-relaxed mb-5">
                <em>When Grief Feels Like Love.</em> Two pages. Tender, honest, made for the messy middle. Drop your email — it lands in your inbox.
              </p>
              <p className="font-sans text-xs tracking-[0.28em] uppercase text-[#d4af6a] font-semibold group-hover:underline underline-offset-4">
                /grief-printable →
              </p>
            </Link>

            {/* Door 3 — Daily card pulls on YouTube */}
            <a
              href="https://www.youtube.com/@MindBodySpiritHygiene"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="door-youtube"
              className="group block bg-white rounded-3xl p-8 shadow-sm border border-[#042F2E]/8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="text-3xl mb-4" aria-hidden="true">✦</div>
              <h3 className="font-serif italic text-2xl text-[#042F2E] mb-3 leading-tight">
                Daily card pulls.
              </h3>
              <p className="font-serif text-[15px] text-[#1E2A29] leading-relaxed mb-5">
                A fresh pull every morning on YouTube. Free. Forever. Subscribe and hit the bell so you don't miss yours.
              </p>
              <p className="font-sans text-xs tracking-[0.28em] uppercase text-[#068fa2] font-semibold group-hover:underline underline-offset-4">
                @MindBodySpiritHygiene →
              </p>
            </a>
          </div>

          {/* Welcome home */}
          <div className="mt-16 text-center" data-testid="welcome-home">
            <p className="font-serif italic text-3xl md:text-4xl text-[#042F2E]">
              Welcome home.
            </p>
          </div>

          {/* Calm calendar opt-in — quiet invitation after the benediction */}
          <div className="mt-12 max-w-2xl mx-auto">
            <BlogCalendarOptin articleSlug="the-messy-middle-is-the-door" />
          </div>
        </div>
      </section>
    </article>
  );
};

export default BlogMessyMiddle;
